import OpenTextQuestion from './open-text-question';
import Event from "../../../event";

/**
 * Message types.
 * - Technical - intro, finish, below or above answer length limits messages, which is not require actual request to language model.
 * - Assistance - message from language model.
 * - Waiting - empty message, which indication start of waiting time before asking language model.
 * @type {Readonly<{Technical: string, Assistance: string, Waiting: string}>}
 */
const AssistedOpenMessageTypes = Object.freeze({
    Waiting: 'Waiting',
    Assistance: 'Assistance',
    Technical: 'Technical',
});

/**
 * @desc Open text question with language model assistance.
 * @extends {OpenTextQuestion}
 */
class AssistedOpenTextQuestion extends OpenTextQuestion {
    /**
     * Create instance.
     * @param {object} model - Raw question model.
     * @param {OpenAssistService} openAssistService - assistance service
     */
    constructor(model, openAssistService) {
        super(model);

        this._openAssistService = openAssistService;
        this._metadata = model.metadata;
        this._promptMaxCount = model.promptMaxCount;
        this._promptDelay = model.promptDelay;
        this._triggerAnswerMinLength = model.triggerAnswerMinLength;
        this._triggerAnswerMaxLength = model.triggerAnswerMaxLength;
        this._introMessage = model.introMessage;
        this._belowMinLengthLimitMessage = model.belowMinLengthLimitMessage;
        this._finishMessage = model.finishMessage;

        this._messageReceiveEvent = new Event('open-assist:onMessageReceive');

        this._currentPromptCount = 0;
        this._lastMessageIsFinishMessage = false;

        this._assistanceReactionDelay = 1000;
        this._assistanceReactionDelayTimeout = null;

        this._assistanceWaitingDelay = (this._promptDelay || 3000 ) - this._assistanceReactionDelay;
        this._assistanceWaitingDelayTimeout = null;
    }

    /**
     * Fired when new assistance message is ready.
     * Event argument object contains type and text of the message.
     * @event changeEvent
     * @type {Event}
     */
    get messageReceiveEvent() {
        return this._messageReceiveEvent;
    }

    /**
     * Manual trigger assistance from the language model.
     * The result should be gotten from subscription on "messageReceiveEvent" event.
     */
    assist() {
        this._checkAndWaitForAssist();
    }

    _onChange(changes, previousState) {
        super._onChange(changes, previousState);

        if(changes.value === true && previousState.value?.trim() === this.value?.trim()) {
            return;
        }

        if(this._assistanceReactionDelay !== null) {
            clearTimeout(this._assistanceReactionDelayTimeout);
        }
        if(this._assistanceWaitingDelayTimeout !== null) {
            clearTimeout(this._assistanceWaitingDelayTimeout);
        }

        this._assistanceReactionDelayTimeout = setTimeout(() => this._checkAndWaitForAssist(), this._assistanceReactionDelay);
    }

    _checkAndWaitForAssist() {
        const answer = this.value || '';
        if (answer.length === 0) {
            this._sendTechnicalMessage(this._introMessage);
            return;
        }
        if (answer.length < this._triggerAnswerMinLength) {
            this._sendTechnicalMessage(this._belowMinLengthLimitMessage);
            return;
        }
        if (answer.length > this._triggerAnswerMaxLength) {
            this._sendFinishMessage();
            return;
        }
        if (this._currentPromptCount >= this._promptMaxCount) {
            this._sendFinishMessage();
            return;
        }

        this._sendWaitingMessage();
        this._assistanceWaitingDelayTimeout = setTimeout(() => this._assist(answer), this._assistanceWaitingDelay);
    }

    async _assist(answer) {
        this._currentPromptCount++;
        let response = null;
        try {
            response = await this._openAssistService.ask(answer, this._metadata);
        } catch(error) {
            // eslint-disable-next-line no-console
            console.error(`Assistance service is not available. Error: ${error.message}`);
            throw error;
        }
        this._sendAssistanceMessage(response.toString());
    }

    _sendAssistanceMessage(message) {
        this._lastMessageIsFinishMessage = false;
        this.messageReceiveEvent.trigger({text: message, type: AssistedOpenMessageTypes.Assistance});
    }

    _sendFinishMessage() {
        if(this._lastMessageIsFinishMessage) {
            return;
        }
        this._lastMessageIsFinishMessage = true;
        this.messageReceiveEvent.trigger({text: this._finishMessage, type: AssistedOpenMessageTypes.Assistance});
    }

    _sendWaitingMessage() {
        this.messageReceiveEvent.trigger({text: null, type: AssistedOpenMessageTypes.Waiting});
    }

    _sendTechnicalMessage(message) {
        this._lastMessageIsFinishMessage = false;
        this.messageReceiveEvent.trigger({text: message, type:  AssistedOpenMessageTypes.Technical});
    }
}

export default AssistedOpenTextQuestion;
export {
    AssistedOpenMessageTypes,
    AssistedOpenTextQuestion,
};
